import SentryClient from '../sentry';
import {
  TrackContentOpenedType,
  TrackContentConsumedType,
  TrackCourseProgressType,
  TrackDisciplineProgressType,
  TrackLessonPlayedType,
  TrackLessonReactedType,
  TrackForumChannelOpenedType,
  TrackForumTopicCreated,
  TrackForumCommentPostedType,
  TrackPageViewType,
  TrackNotificationInterectedType,
  TrackNotificationViewedType,
  TrackPrivacyPolicyType,
  TrackSmartBannerType,
  TrackStudyPlanInteracted,
  TrackTermsOfUse,
  TrackOverdueUserWarningModalInteraction,
  TrackOverdueUserWarningModalViewed,
  TrackMicrocertificateInteraction,
  TrackCompleteWeeklyListsCardInteraction,
  TrackMenuClicked,
  TrackHelpCenterArticles,
  TrackAccessibilityIconClick,
  TrackReportCardToggle,
  TrackReportCardView,
  TrackReportCardActivityClick,
  TrackViralLoopsActivity,
} from './types';
import Trial from '@/services/Trial';

type TrackData =
  | TrackContentConsumedType
  | TrackContentOpenedType
  | TrackCourseProgressType
  | TrackDisciplineProgressType
  | TrackLessonPlayedType
  | TrackLessonReactedType
  | TrackForumChannelOpenedType
  | TrackForumTopicCreated
  | TrackForumCommentPostedType
  | TrackPageViewType
  | TrackNotificationViewedType
  | TrackNotificationInterectedType
  | TrackPrivacyPolicyType
  | TrackSmartBannerType
  | TrackStudyPlanInteracted
  | TrackTermsOfUse
  | TrackOverdueUserWarningModalInteraction
  | TrackOverdueUserWarningModalViewed
  | TrackMicrocertificateInteraction
  | TrackCompleteWeeklyListsCardInteraction
  | TrackMenuClicked
  | TrackHelpCenterArticles
  | TrackAccessibilityIconClick
  | TrackReportCardToggle
  | TrackReportCardView
  | TrackReportCardActivityClick
  | TrackViralLoopsActivity;

interface UserIdentify {
  id: number;
  email: string;
  name: string;
  flags?: { [key: string]: string | boolean };
  courses?: string[];
  categories?: string[];
}

interface PocketOptions {
  useMixpanel: boolean;
  useHubspot: boolean;
}

interface UserCourse {
  name: string | null;
  category: string | null;
}

export const POCKET_SETTINGS = {
  hubspot: process.env.VUE_APP_HUBSPOT_ID,
  mixpanel: process.env.VUE_APP_MIXPANEL_TOKEN,
  enableMixpanel: true,
  enableHubspot: true,
  enableSegment: false,
};

function checkPocketLoad(methodName: string): void {
  const errorMessage = `POCKET did not load in ${methodName} yet`;
  if (!window || !window.POCKET) {
    throw new Error(errorMessage);
  }

  if (!window.POCKET_ENV.mixpanel) {
    window.POCKET.updateSettings(POCKET_SETTINGS);
  }
}

class Analytics {
  static initMixpanelFailsafe() {
    if (!window || !window.mixpanel) {
      throw new Error('Mixpanel is not loaded yet');
    }

    if (!window.POCKET_ENV.mixpanel) {
      throw new Error('POCKET has not been initialized');
    }

    window.mixpanel.init(window.POCKET_ENV.mixpanel);
  }

  static identify(
    data: UserIdentify,
    options: PocketOptions = { useHubspot: true, useMixpanel: true },
  ) {
    checkPocketLoad('identify');

    const { id, email, name, flags, courses, categories } = data;
    const storedProfile = window.POCKET.profile() || null;
    const userProfile = {
      id: (storedProfile && storedProfile.id) || id,
      email: (storedProfile && storedProfile.email) || email,
      name: (storedProfile && storedProfile.name) || name || 'Aluno do Descomplica',
    };

    window.POCKET.identify(
      userProfile.id.toString(),
      {
        email: userProfile.email,
        name: userProfile.name,
        $email: userProfile.email,
        courses: courses || [],
        categories: categories || [],
        ...flags,
      },
      options,
    );
  }

  static updateUserCourses(course: UserCourse) {
    try {
      checkPocketLoad('updateUserCourses');
      const storedProfile = window.POCKET.profile() || null;

      if (!storedProfile) {
        throw new Error('Could not identify user in updateUserCourses');
      }

      window.POCKET.identify(storedProfile.id.toString(), {
        course: course.name,
        category: course.category,
        ...storedProfile,
      });
    } catch (err: any) {
      SentryClient.captureError(err);
    }
  }

  static trackEvent(eventName: string, trackData: TrackData | {} = {}): void {
    try {
      checkPocketLoad('trackEvent');
      window.POCKET.track(eventName, trackData);
    } catch (err: any) {
      SentryClient.captureError(err);
    }
  }

  static trackTimeEvent(eventName: string): void {
    try {
      checkPocketLoad('trackTimeEvent');
      window.POCKET.timeEvent(eventName);
    } catch (err: any) {
      SentryClient.captureError(err);
    }
  }

  static getRawUrl(): string {
    const { host, pathname } = window.location;
    return `${host}${pathname}`;
  }

  static trackPageView(): void {
    this.trackEvent('Page View', {
      gt_Location: this.getRawUrl(),
    });
  }

  static flagsBuilder(featureFlags?: { [key: string]: string | boolean }): {
    [key: string]: string | boolean;
  } {
    if (!featureFlags) return {};
    return {
      has_migrated_to_classrooms: featureFlags['redirect-to-classrooms'],
    };
  }

  static async registerIfIsInTrial() {
    checkPocketLoad('registerIfIsInTrial');

    if (
      (await Trial.isInTrial(window.POCKET.profile()?.email || null)) &&
      (await Trial.isInTrialTracking())
    ) {
      window.POCKET.registerMixpanel({ in_graduation_trial: true });
    } else {
      window.POCKET.unregisterMixpanel('in_graduation_trial');
    }
  }

  static async trackEventInTrial(eventName: string, trackData: TrackData) {
    if (
      (await Trial.isInTrial(window.POCKET.profile()?.email || null)) &&
      (await Trial.isInTrialTracking())
    ) {
      this.trackEvent(eventName, trackData);
    }
  }
}

export default Analytics;
